import React, { useState, useEffect } from "react";
import { Button, Form, Container, Row, Col } from "react-bootstrap";
import Select from "react-dropdown-select";
import moment from "moment";
import axios from "axios";
import { config } from "../../App";

const Loader = () => (
    <div className="loader-container">
        <img src="./images/loader.gif" alt="loading" />
    </div>
);

const imageUrlToBase64 = (url) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            canvas.getContext("2d").drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png").split(",")[1]);
        };
        img.onerror = reject;
        img.src = url;
    });

const embedWatermarkInPdf = async (pdfBytes, watermarkBase64) => {
    if (!window.PDFLib) {
        await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf-lib/1.17.1/pdf-lib.min.js";
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
        });
    }

    const { PDFDocument } = window.PDFLib;

    const pdfDoc = await PDFDocument.load(pdfBytes);
    const logoBytes = Uint8Array.from(atob(watermarkBase64), (c) => c.charCodeAt(0));
    const logoImage = await pdfDoc.embedPng(logoBytes);

    for (const page of pdfDoc.getPages()) {
        const { width, height } = page.getSize();
        const desiredWidth = width * 0.4;
        const scale = desiredWidth / logoImage.width;
        const imgWidth = logoImage.width * scale;
        const imgHeight = logoImage.height * scale;

        page.drawImage(logoImage, {
            x: (width - imgWidth) / 2,
            y: (height - imgHeight) / 2,
            width: imgWidth,
            height: imgHeight,
            opacity: 0.15,
        });
    }

    return pdfDoc.save();
};

// filter out June (month value 6) of year 2026 from a list of month
const filterHiddenMonths = (yearValue, monthsArr) =>
    monthsArr.filter((m) => !(yearValue === 2026 && (m.MonthValue ?? m.value) === 6));

const PaySlip = () => {

    const token = sessionStorage.getItem("token");
    const userId = sessionStorage.getItem("userId");
    const doj = sessionStorage.getItem("doj");
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [loading, setLoading] = useState(false);
    const [yearError, setYearError] = useState("");
    const [monthError, setMonthError] = useState("");
    const [success, setSuccess] = useState("");
    const [years, setYears] = useState([]);
    const [months, setMonths] = useState([]);

    const [pdfBlob, setPdfBlob] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [isSearched, setIsSearched] = useState(false);

    const [taxRegimeType, setTaxRegimeType] = useState("");
    const [rawPdfBytes, setRawPdfBytes] = useState(null);

    const getGetTaxRegimeType = async () => {
        try {
            const response = await axios.get(
                `${config.endpoint}/payroll/GetTaxRegimeType?employeeId=${userId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = response.data;
            if (!data?.status) return;
            setTaxRegimeType(data.Data.TaxRegimeType);

        } catch (err) {
            console.error("fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const getCalendarData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${config.endpoint}/payroll/GetCalendarDetail?employeeId=${userId}&doj=${moment(doj, "DD-MMM-YYYY").format("DD-MM-YYYY")}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = response.data;
            if (!data?.Status) return;

            const calendarData = data.CalendarData || [];

            const formattedYears = calendarData.map((y) => ({
                label: y.Year.toString(),
                value: y.Year,
                months: y.Months,
            }));

            setYears(formattedYears);

            const defaultYear = parseInt(data.DefaultYear);
            const defaultMonth = parseInt(data.DefaultMonth);

            const defaultYearObj = formattedYears.find(
                (y) => y.value === defaultYear
            );

            if (defaultYearObj) {
                setSelectedYear(defaultYearObj);

                // Hide June (month 6) of year 2026
                const visibleMonths = filterHiddenMonths(defaultYearObj.value, defaultYearObj.months);

                const formattedMonths = visibleMonths.map((m) => ({
                    label: m.MonthName,
                    value: m.MonthValue,
                }));

                setMonths(formattedMonths);

                const defaultMonthObj = formattedMonths.find(
                    (m) => m.value === defaultMonth
                );

                setSelectedMonth(defaultMonthObj || null);
            }

        } catch (err) {
            console.error("fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleYearChange = (vals) => {
        const year = vals[0] || null;
        setSelectedYear(year);
        setYearError("");

        if (!year) {
            setMonths([]);
            setSelectedMonth(null);
            return;
        }

        // Hide June (month 6) of year 2026
        const visibleMonths = filterHiddenMonths(year.value, year.months);

        const formattedMonths = visibleMonths.map((m) => ({
            label: m.MonthName,
            value: m.MonthValue,
        }));

        setMonths(formattedMonths);

        if (
            selectedMonth &&
            !formattedMonths.find((m) => m.value === selectedMonth.value)
        ) {
            setSelectedMonth(null);
        }
    };

    useEffect(() => {
        getCalendarData();
        getGetTaxRegimeType();
    }, []);

    useEffect(() => {
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [pdfUrl]);

    const validateForm = () => {
        let isValid = true;
        setYearError("");
        setMonthError("");

        if (!selectedYear) {
            setYearError("Please select a Year");
            isValid = false;
        }

        if (!selectedMonth) {
            setMonthError("Please select a Month");
            isValid = false;
        }

        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSearched(true);
        setSuccess("");
        setPdfBlob(null);
        setRawPdfBytes(null);

        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }

        if (!validateForm()) {
            return;
        }

        // Safety guard: block June 2026 even if somehow selected
        if (selectedYear.value === 2026 && selectedMonth.value === 6) {
            setMonthError("Pay slip for this month is temporarily unavailable.");
            return;
        }

        const payload = {
            employeeId: userId, //"115553",
            Month: selectedMonth.value,
            Year: selectedYear.value,
            type: "payslip",
        };

        setLoading(true);

        try {
            const response = await axios.post(`${config.endpoint}/payroll/Getpayroll`, payload, {
                //responseType: "blob",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const data = response.data;

            if (!data?.Status) {
                throw new Error(data?.Message || "API returned unsuccessful status");
            } else {

            }

            const base64String = data.Pdfbytes;
            if (!base64String || typeof base64String !== "string") {
                throw new Error("No valid Pdfbytes received");
            }

            const byteCharacters = atob(base64String);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/pdf" });

            const objectUrl = URL.createObjectURL(blob);

            setPdfBlob(blob);
            setPdfUrl(objectUrl);
            setRawPdfBytes(byteArray.buffer);
            setSuccess(`Pay slip for ${selectedMonth.label} ${selectedYear.value} loaded successfully`);
        } catch (err) {
            console.error("Pay slip fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!rawPdfBytes) return;

        setLoading(true);
        try {
            const watermarkBase64 = await imageUrlToBase64("./images/logo-3.png");
            const watermarkedBytes = await embedWatermarkInPdf(rawPdfBytes, watermarkBase64);

            const blob = new Blob([watermarkedBytes], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const fileName = `Payslip_${selectedMonth?.label || "month"}_${selectedYear?.value || "year"}.pdf`;
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Watermark embed error:", err);
            alert("Failed to embed watermark. Downloading without watermark.");

            // Fallback: download original
            const fileName = `Payslip_${selectedMonth?.label || "month"}_${selectedYear?.value || "year"}.pdf`;
            const link = document.createElement("a");
            link.href = pdfUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <section className="px-4">
                {loading && <Loader />}
                {loading && <div className="backdrop" />}
                <Container fluid>
                    <div className="card border-0 p-4 pb-2">
                        <h1 className="fs-22 fw-semibold mb-4">Pay Slip</h1>
                        <Row>
                            {taxRegimeType !== "" && (
                                <Col lg={12} className="mb-4">
                                    <div
                                        style={{
                                            background: "linear-gradient(135deg, #eef4ff 0%, #f5f0ff 100%)",
                                            border: "1px solid #c7d9f8",
                                            borderLeft: "5px solid #f97316",
                                            borderRadius: "8px",
                                            padding: "16px 20px",
                                            display: "flex",
                                            gap: "14px",
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        <div
                                            style={{
                                                borderRadius: "50%",
                                                width: "36px",
                                                height: "36px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="#f97316"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="8" x2="12" y2="8" strokeWidth="3" />
                                                <line x1="12" y1="12" x2="12" y2="16" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="fw-semibold mb-1" style={{ color: "#1a3c6e", fontSize: "15px", marginTop: "7px" }}>
                                                You are under the <strong>{taxRegimeType}</strong>.
                                            </p>
                                            <p className="" style={{ color: "#f01023", fontSize: "15px", marginTop: "2px" }}>
                                                The June 2026 payslip is temporarily unavailable. It will be available shortly.
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            )}

                            <Col lg={4} className="mb-4 pb-4">
                                <Form.Group controlId="year">
                                    <Form.Label className="mb-2">
                                        Year <sup className="primary-text font-md">*</sup>
                                    </Form.Label>
                                    <Select
                                        options={years}
                                        values={selectedYear ? [selectedYear] : []}
                                        onChange={handleYearChange}
                                        placeholder="Select Year"
                                        className="form-control shadow-none"
                                        dropdownHeight="240px"
                                        searchable
                                    />
                                    {yearError && (
                                        <div className="text-danger mt-1 small font-sm position-absolute">{yearError}</div>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col lg={4} className="mb-4 pb-4">
                                <Form.Group controlId="month">
                                    <Form.Label className="mb-2">
                                        Month <sup className="primary-text font-md">*</sup>
                                    </Form.Label>
                                    <Select
                                        options={months}
                                        values={selectedMonth ? [selectedMonth] : []}
                                        onChange={(vals) => {
                                            setSelectedMonth(vals[0] || null);
                                            setMonthError("");
                                        }}
                                        placeholder="Select Month"
                                        className="form-control"
                                        dropdownHeight="240px"
                                    />
                                    {monthError && (
                                        <div className="text-danger mt-1 small font-sm position-absolute">{monthError}</div>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col lg={4} className="mb-4 d-flex align-items-end pb-0 pb-xl-4 pb-lg-4">
                                <div className="d-flex gap-3">
                                    <Button type="submit" onClick={handleSubmit} className="btn btn-primary font-lg">
                                        Submit
                                    </Button>
                                    {pdfUrl && (
                                        <Button onClick={handleDownload} type="button" className="btn btn-secondary font-lg">
                                            Download
                                        </Button>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="pdfViewSec mt-5 mb-5 card border-0 p-4">
                        <Row>
                            <Col lg={12}>
                                {pdfUrl && (
                                    <div
                                        style={{
                                            position: "relative",
                                            height: "800px",
                                            border: "1px solid #ced4da",
                                            borderRadius: "6px",
                                            overflow: "hidden",
                                            background: "#f8f9fa",
                                        }}
                                    >
                                        <img
                                            src="./images/logo-3.png"
                                            alt="watermark"
                                            style={{
                                                position: "absolute",
                                                top: "50%",
                                                left: "50%",
                                                transform: "translate(-50%, -50%)",
                                                opacity: 0.15,
                                                pointerEvents: "none",
                                                zIndex: 16,
                                                width: "40%",
                                            }}
                                        />
                                        <iframe
                                            src={`${pdfUrl}#toolbar=0&navpanes=0`}
                                            title={`Pay Slip ${selectedMonth?.label || ""} ${selectedYear?.value || ""}`}
                                            width="100%"
                                            height="100%"
                                            style={{
                                                border: "none",
                                                position: "relative",
                                                zIndex: 2,
                                            }}
                                        />
                                    </div>
                                )}

                                {isSearched && !pdfUrl && !loading && (
                                    <div className="card border-0 p-4 text-center text-muted font-md">
                                        <img
                                            src="images/data-not-found.svg"
                                            className="img-fluid m-auto mt-5 mb-5 pt-5"
                                            alt="No data"
                                        />
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>
                </Container>

            </section>
        </>
    )
}

export default PaySlip;